import { Injectable } from '@angular/core';

export interface ApiActionLog {
  timestamp: Date;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  payload?: any;
  actionType: string;
  driverId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class DriverApiTrackerService {
  private actionLogs: ApiActionLog[] = [];
  private callbacks: ((log: ApiActionLog) => void)[] = [];
  private enabled = false;

  /**
   * Enable tracking - call this when entering edit driver component
   */
  enableTracking(): void {
    console.log('🟢 API Tracking ENABLED');
    this.enabled = true;
  }

  /**
   * Disable tracking - call this when leaving edit driver component
   */
  disableTracking(): void {
    console.log('🔴 API Tracking DISABLED');
    this.enabled = false;
    this.callbacks = []; // Clear all callbacks
    this.actionLogs = []; // Clear logs
  }

  /**
   * Check if tracking is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Track an API action (called by interceptor)
   */
  trackAction(
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    endpoint: string,
    actionType: string,
    payload?: any,
    driverId?: number,
  ): void {
    if (!this.enabled) {
      return; // Don't track if disabled
    }

    const log: ApiActionLog = {
      timestamp: new Date(),
      method,
      endpoint,
      actionType,
      payload,
      driverId,
    };

    this.actionLogs.push(log);
    this.notifyCallbacks(log);
  }

  /**
   * Subscribe to API action events
   * @param callback - Function to call when an action is logged
   * @returns Unsubscribe function
   */
  onApiAction(callback: (log: ApiActionLog) => void): () => void {
    this.callbacks.push(callback);
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Get all logged actions
   */
  getActionLogs(): ApiActionLog[] {
    return [...this.actionLogs];
  }

  /**
   * Get actions for a specific driver
   */
  getActionLogsByDriver(driverId: number): ApiActionLog[] {
    return this.actionLogs.filter(log => log.driverId === driverId);
  }

  /**
   * Clear all action logs
   */
  clearLogs(): void {
    this.actionLogs = [];
  }

  /**
   * Notify all registered callbacks
   */
  private notifyCallbacks(log: ApiActionLog): void {
    this.callbacks.forEach(callback => callback(log));
  }
}
